import { _ as _object_spread } from "@swc/helpers/_/_object_spread";
import { _ as _object_spread_props } from "@swc/helpers/_/_object_spread_props";
import { jsx as _jsx, Fragment as _Fragment } from "react/jsx-runtime";
import { useCallback, useMemo, useState } from 'react';
import { useFormContext } from 'react-hook-form';
import { useTemplateVariableDetection } from '~/components/AgreementStudioPage/TemplateVariables/hooks/useTemplateVariableDetection';
import { normalizeVariableLabel } from '~/components/AgreementStudioPage/TemplateVariables/TemplateVariables.utils';
import { TemplateVariablesPortalProvider, useHasTemplateVariablesProvider, useTemplateVariables } from '~/components/AgreementStudioPage/TemplateVariables/TemplateVariablesContext';
export function EditServiceContentModalVariablesAugmenter(param) {
    let { children } = param;
    const hasProvider = useHasTemplateVariablesProvider();
    if (!hasProvider) {
        return /*#__PURE__*/ _jsx(_Fragment, {
            children: children
        });
    }
    return /*#__PURE__*/ _jsx(AugmenterBody, {
        children: children
    });
}
// eslint-disable-next-line r21/no-multi-comp -- AugmenterBody is a private implementation artifact to avoid calling hooks conditionally (React rules of hooks); not a standalone component
function AugmenterBody(param) {
    let { children } = param;
    const inherited = useTemplateVariables();
    const { watch } = useFormContext();
    const [serviceName, description, terms] = watch([
        'serviceName',
        'description',
        'terms'
    ]);
    const textFields = useMemo(()=>[
            serviceName !== null && serviceName !== void 0 ? serviceName : ''
        ], [
        serviceName
    ]);
    const richTextFields = useMemo(()=>[
            description !== null && description !== void 0 ? description : '',
            terms !== null && terms !== void 0 ? terms : ''
        ], [
        description,
        terms
    ]);
    const formLocal = useTemplateVariableDetection(textFields, richTextFields);
    const [localOverrides, setLocalOverrides] = useState({});
    const { setTemplateVariableValue: inheritedSetTemplateVariableValue } = inherited;
    const localSetTemplateVariableValue = useCallback((label, value)=>{
        setLocalOverrides((prev)=>_object_spread_props(_object_spread({}, prev), {
                [normalizeVariableLabel(label)]: value
            }));
        inheritedSetTemplateVariableValue(label, value);
    }, [
        inheritedSetTemplateVariableValue
    ]);
    const effectiveTemplateVariableValues = useMemo(()=>_object_spread({}, inherited.templateVariableValues, localOverrides), [
        inherited.templateVariableValues,
        localOverrides
    ]);
    const merged = useMemo(()=>mergeContexts(inherited, formLocal, effectiveTemplateVariableValues, localSetTemplateVariableValue), [
        inherited,
        formLocal,
        effectiveTemplateVariableValues,
        localSetTemplateVariableValue
    ]);
    return /*#__PURE__*/ _jsx(TemplateVariablesPortalProvider, {
        value: merged,
        children: children
    });
}
function mergeContexts(inherited, formLocal, effectiveTemplateVariableValues, setTemplateVariableValue) {
    const customVariables = unionByNormalizedLabel(inherited.customVariables, formLocal.customVariables);
    const detectedVariables = unionByNormalizedLabel(inherited.detectedVariables, formLocal.detectedVariables);
    const systemVariables = unionByNormalizedLabel(inherited.systemVariables, formLocal.systemVariables);
    const resolvedValues = mergeResolvedValues(inherited.resolvedValues, customVariables, effectiveTemplateVariableValues);
    const unresolvedCustomVariables = customVariables.filter((label)=>resolvedValues[normalizeVariableLabel(label)] == null);
    return {
        detectedVariables,
        customVariables,
        systemVariables,
        resolvedValues,
        templateVariableValues: effectiveTemplateVariableValues,
        setTemplateVariableValue,
        unresolvedCustomVariables,
        hasVariables: detectedVariables.length > 0,
        showVariableErrors: inherited.showVariableErrors,
        setShowVariableErrors: inherited.setShowVariableErrors
    };
}
function unionByNormalizedLabel(first, second) {
    const seen = new Set(first.map(normalizeVariableLabel));
    const result = [
        ...first
    ];
    for (const label of second){
        const normalized = normalizeVariableLabel(label);
        if (!seen.has(normalized)) {
            seen.add(normalized);
            result.push(label);
        }
    }
    return result;
}
function mergeResolvedValues(inheritedResolvedValues, mergedCustomVariables, effectiveTemplateVariableValues) {
    const result = _object_spread({}, inheritedResolvedValues);
    for (const label of mergedCustomVariables){
        const normalized = normalizeVariableLabel(label);
        if (result[normalized] != null) continue;
        const userValue = effectiveTemplateVariableValues[normalized];
        if (userValue != null) {
            result[normalized] = userValue;
        }
    }
    return result;
}
